/**
 * RevealText: DOM wrap + GSAP ScrollTrigger for .reveal-down, .reveal-char, .reveal-wrap.
 * Selectors are injected from outside. Uses once: true so animation does not reset.
 */
import type { ElementWithRevealInit } from '../../types';

export interface RevealTextSelectors {
  revealDown?: string;
  revealChar?: string;
  revealWrap?: string;
  heroTitle?: string;
}

const DEFAULT_SELECTORS: Required<RevealTextSelectors> = {
  revealDown: '.reveal-down',
  revealChar: '.reveal-char',
  revealWrap: '.reveal-wrap',
  heroTitle: '.no-main-hero__slider .swiper-wrapper .swiper-slide .video-link .no-title',
};

type ElementWithRdInit = Element & { dataset: { rdInit?: string }; __rcInit?: boolean };
type ElementWithRwInit = Element & ElementWithRevealInit;

export class RevealText {
  private readonly selectors: Required<RevealTextSelectors>;
  private readonly root: Document | Element;
  private mo: MutationObserver | null = null;

  constructor(
    selectors: RevealTextSelectors = {},
    root: Document | Element = document
  ) {
    this.selectors = { ...DEFAULT_SELECTORS, ...selectors };
    this.root = root;
  }

  /**
   * 초기화: DOM 래핑 + GSAP 설정 + 동적 노드 감시
   */
  init(): void {
    this.wrapRevealDownAll();
    this.setupRevealChar();
    this.setupRevealWrap();
    this.observe();
    if (typeof window !== 'undefined') {
      window.initRevealDown = () => this.initRevealDown();
    }
  }

  /**
   * reveal-down: .rd > .char.a, .char.b 래핑 (CSS 애니메이션용)
   */
  private wrapRevealDown(el: Element): void {
    if ((el as ElementWithRdInit).dataset?.rdInit === '1' || el.querySelector(':scope > .rd')) return;
    const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text) return;

    (el as ElementWithRdInit).dataset.rdInit = '1';
    el.setAttribute('aria-label', text);
    el.textContent = '';

    const frag = document.createDocumentFragment();
    for (let i = 0; i < text.length; i++) {
      const ch = text[i] === ' ' ? '\u00A0' : text[i];
      const rd = document.createElement('span');
      rd.className = 'rd';
      const a = document.createElement('span');
      a.className = 'char a';
      (a as HTMLElement).style.setProperty('--i', String(i));
      a.textContent = ch;
      const b = document.createElement('span');
      b.className = 'char b';
      (b as HTMLElement).style.setProperty('--i', String(i));
      b.textContent = ch;
      rd.appendChild(a);
      rd.appendChild(b);
      frag.appendChild(rd);
    }
    el.appendChild(frag);

    const elHtml = el as HTMLElement;
    if (elHtml.dataset.rdDur) elHtml.style.setProperty('--dur', elHtml.dataset.rdDur);
    if (elHtml.dataset.rdStagger) elHtml.style.setProperty('--stagger', elHtml.dataset.rdStagger);
    if (elHtml.dataset.rdLift) elHtml.style.setProperty('--lift', elHtml.dataset.rdLift);
  }

  /**
   * hero 전용: 단어 단위 .wd > .rd > .char.a, .char.b
   */
  private wrapHeroWords(el: Element): void {
    if (el.querySelector(':scope > .wd')) return;
    const text = (el.getAttribute('aria-label') || el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text) return;
    el.setAttribute('aria-label', text);
    el.textContent = '';

    const words = text.split(' ');
    const frag = document.createDocumentFragment();
    let idx = 0;
    words.forEach((w, wi) => {
      const wd = document.createElement('span');
      wd.className = 'wd';
      for (let k = 0; k < w.length; k++) {
        const rd = document.createElement('span');
        rd.className = 'rd';
        const a = document.createElement('span');
        a.className = 'char a';
        (a as HTMLElement).style.setProperty('--i', String(idx));
        a.textContent = w[k];
        const b = document.createElement('span');
        b.className = 'char b';
        (b as HTMLElement).style.setProperty('--i', String(idx));
        b.textContent = w[k];
        rd.appendChild(a);
        rd.appendChild(b);
        wd.appendChild(rd);
        idx++;
      }
      frag.appendChild(wd);
      if (wi !== words.length - 1) frag.appendChild(document.createTextNode(' '));
    });
    el.appendChild(frag);
  }

  private getText(el: Element): string {
    return (el.getAttribute('aria-label') || el.textContent || '').replace(/\s+/g, ' ').trim();
  }

  private wrapRevealDownAll(): void {
    const { revealDown, heroTitle } = this.selectors;
    const heroSel = heroTitle;
    const root = this.root;

    root.querySelectorAll(heroSel).forEach((el) => {
      if (el.querySelector(':scope > .rd')) (el as HTMLElement).innerHTML = '';
      this.wrapHeroWords(el);
    });
    root.querySelectorAll(revealDown).forEach((el) => {
      if (!el.matches(heroSel)) this.wrapRevealDown(el);
    });
  }

  /**
   * reveal-char: 단어/글자 분할 + GSAP fromTo, once: true
   */
  private setupRevealChar(): void {
    const gsap = window.gsap;
    const ST = window.ScrollTrigger;
    if (!gsap || !ST) return;

    gsap.registerPlugin(ST);
    const scroller = window.lenis ? document.documentElement : undefined;

    const splitByWord = (el: Element): void => {
      const elInit = el as ElementWithRdInit;
      if (elInit.__rcInit) return;
      const text = (el.textContent || '').trim();
      if (!text) return;
      el.setAttribute('aria-label', text);
      el.textContent = '';
      const frag = document.createDocumentFragment();
      text.split(/(\s+)/).forEach((tok) => {
        if (/\s+/.test(tok)) {
          frag.appendChild(document.createTextNode(tok));
        } else {
          const w = document.createElement('span');
          w.className = 'word';
          [...tok].forEach((ch) => {
            const s = document.createElement('span');
            s.className = 'char';
            s.textContent = ch;
            w.appendChild(s);
          });
          frag.appendChild(w);
        }
      });
      el.appendChild(frag);
      elInit.__rcInit = true;
    };

    const opts = { start: 'top 90%', duration: 0.6, ease: 'power2.out', stagger: 0.04, once: true };
    const scrollTriggerConfig = scroller
      ? { trigger: null as unknown as Element, start: opts.start, scroller, once: opts.once }
      : { trigger: null as unknown as Element, start: opts.start, once: opts.once };

    this.root.querySelectorAll(this.selectors.revealChar).forEach((el) => {
      splitByWord(el);
      const chars = el.querySelectorAll('.char');
      if (chars.length === 0) return;
      const config = { ...scrollTriggerConfig, trigger: el };
      gsap.fromTo(
        Array.from(chars),
        { y: '100%' },
        { y: '0', duration: opts.duration, ease: opts.ease, stagger: opts.stagger, scrollTrigger: config }
      );
    });
  }

  /**
   * reveal-wrap: .inner 래핑 + GSAP fromTo, once: true
   */
  private setupRevealWrap(): void {
    const gsap = window.gsap;
    const ST = window.ScrollTrigger;
    if (!gsap || !ST) return;

    const ensureInner = (p: Element): void => {
      const el = p as ElementWithRwInit;
      if (!p || el.__rwInit) return;
      const t = (p.textContent || '').trim();
      const s = document.createElement('span');
      s.className = 'inner';
      s.textContent = t;
      p.textContent = '';
      p.appendChild(s);
      el.__rwInit = true;
    };

    const opts = { start: 'top 85%', duration: 0.8, ease: 'power2.out', stagger: 0.12, once: true };
    const scroller = window.lenis ? document.documentElement : undefined;
    const scrollTriggerConfig = scroller
      ? { trigger: null as unknown as Element, start: opts.start, scroller, once: opts.once }
      : { trigger: null as unknown as Element, start: opts.start, once: opts.once };

    this.root.querySelectorAll(this.selectors.revealWrap).forEach((wrap) => {
      const items = [...wrap.querySelectorAll('.reveal')];
      items.forEach(ensureInner);
      const inners = items.map((p) => p.querySelector('.inner')).filter((x): x is Element => x != null);
      if (inners.length === 0) return;
      const config = { ...scrollTriggerConfig, trigger: wrap };
      gsap.fromTo(
        inners,
        { y: '100%' },
        { y: '0%', duration: opts.duration, ease: opts.ease, stagger: opts.stagger, scrollTrigger: config }
      );
    });
  }

  private observe(): void {
    const { revealDown, heroTitle } = this.selectors;
    const root = this.root;
    const doc = root instanceof Document ? root : root.ownerDocument;
    if (!doc) return;

    this.mo = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((n) => {
          if (!(n instanceof Element)) return;
          if (n.matches(heroTitle)) {
            if (n.querySelector(':scope > .rd')) (n as HTMLElement).innerHTML = '';
            this.wrapHeroWords(n);
          }
          n.querySelectorAll(heroTitle).forEach((x) => {
            if (x.querySelector(':scope > .rd')) (x as HTMLElement).innerHTML = '';
            this.wrapHeroWords(x);
          });
          if (n.matches(revealDown) && !n.matches(heroTitle)) this.wrapRevealDown(n);
          n.querySelectorAll(`${revealDown}:not(${heroTitle})`).forEach((el) => this.wrapRevealDown(el));
        });
      });
    });
    this.mo.observe(doc.documentElement, { childList: true, subtree: true });
  }

  /**
   * 동적 추가된 노드에 대해 reveal-down만 다시 실행 (예: Swiper clone).
   * init() 시 window.initRevealDown 에 바인딩해 두면 외부에서 호출 가능.
   */
  initRevealDown(): void {
    this.wrapRevealDownAll();
  }

  destroy(): void {
    if (this.mo) {
      this.mo.disconnect();
      this.mo = null;
    }
  }
}
